import { useEffect, useMemo } from 'react'
import type { FilterStateT } from 'components/Filter/types'
import useFavurTranslation from 'hooks/useFavurTranslation'
import useRolesViews from 'hooks/useRolesViews'
import { useHistory } from 'react-router'
import useTasksQueryPaginated from 'redesign/shared/hooks/useTasksQueryPaginated'
import routes from 'services/RoutesProvider/routes'
import { TASK_LIMIT } from '../../components/templates/ContentLists/TaskList/constants'
import type { UseTaskListProps } from './types'

export const useTaskList = ({ initialFilters = {}, isOffice, isLoadingConfig }: UseTaskListProps) => {
  const history = useHistory()
  const { t } = useFavurTranslation()
  const {
    updateFilters,
    updateOffice,
    hasMoreElements,
    list: tasks,
    loading: tasksLoading,
    loadMore,
    reload,
    totalCount,
    hasFilters,
  } = useTasksQueryPaginated({
    initialOffice: isOffice,
    limit: TASK_LIMIT,
    initialFilters,
    reducedFields: true,
    skip: isLoadingConfig,
  })
  const { persons } = useRolesViews()
  const isShareUserDataManager = useMemo(
    () => persons?.some((person) => person.permissions.workflowShareUserData),
    [persons],
  )
  const showAddTaskButton = isOffice && Boolean(isShareUserDataManager)
  const moreButtonOptions = useMemo(
    () => [
      {
        label: t('requestEmployeeData.common.overTitle'),
        onClick: () => history.push(routes.requestEmployeeData),
      },
    ],
    [history, t],
  )

  const handleFilterChange = (filters: FilterStateT) => updateFilters(filters)

  const isLoading = useMemo(() => tasksLoading || isLoadingConfig, [isLoadingConfig, tasksLoading])

  useEffect(() => {
    updateOffice(isOffice)
  }, [isOffice, updateOffice])

  return {
    isOffice,
    updateFilters,
    updateOffice,
    hasMoreElements,
    handleFilterChange,
    list: tasks ?? [],
    loading: isLoading,
    loadMore,
    reload,
    showLoadMoreButton: (totalCount === undefined || hasMoreElements) && Boolean(tasks.length),
    hasFilters,
    showAddTaskButton,
    moreButtonOptions,
  }
}
